import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { CurrentUserSelectors } from '@core/account-data';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { GuardedFeature } from '@core/feature-guard-legacy';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  type Step,
  WalkthroughState,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class WalkthroughSalutationFieldsService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);

  private readonly currentUser = this.store.selectSignal(CurrentUserSelectors.data);
  private readonly hasCandidateSalutationFieldsAvailable =
    this.featureSpecDataHelpers.selectIsEntitled(GuardedFeature.CandidateSalutationFields);

  private steps: Step[] = [
    {
      id: 'walkthrough-step-salutation-field-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_1.description',
          ),
          ctaText: this.i18n.translate('app.common.got_it'),
          hasNewBadge: true,
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }],
        offset: { offsetX: 0, offsetY: -15 },
      },
      disableOnboardingEndpoint: true,
    },
    {
      id: 'walkthrough-step-salutation-field-2',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['left', 'top'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_2.description',
          ),
          ctaText: this.i18n.translate('app.common.got_it'),
          hasNewBadge: true,
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'start', overlayY: 'top' }],
        offset: { offsetX: -43, offsetY: 20 },
      },
      disableOnboardingEndpoint: true,
    },
    {
      id: 'walkthrough-step-salutation-field-3',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.salutation_fields.step_2.description',
          ),
          ctaText: this.i18n.translate('app.common.got_it'),
          hasNewBadge: true,
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 36, offsetY: 30 },
      },
      disableOnboardingEndpoint: true,
    },
  ];

  public start(step: 1 | 2 | 3): void {
    if (
      this.hasCandidateSalutationFieldsAvailable() &&
      !this.currentUser()?.featureDiscovery?.candidateSalutationFieldsUsed
    ) {
      this.walkthroughStepsService
        .start([this.steps[step - 1]])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(state => {
          if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(state)) {
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { candidateSalutationFieldsUsed: true } }),
            );
          }
        });
    }
  }
}
